/**
 * Brand Service
 * Handles all brand-related API calls including CRUD operations,
 * listing, filtering, and searching brands
 */

import { apiClient, buildQueryParams, ApiResponse } from './api.client';
import { unwrapListResponse } from './api.unwrap';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  ListBrandsRequest,
  PaginatedResponse,
} from '@creatoros/shared';

/**
 * Brand Service Class
 */
class BrandService {
  private readonly basePath = '/api/brands';

  /**
   * Get all brands with optional filtering and pagination
   * @param params - List parameters including filters and pagination
   * @returns Paginated list of brands
   */
  async list(params?: ListBrandsRequest): Promise<PaginatedResponse<Brand>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get(this.basePath, { params: queryParams });

    return unwrapListResponse<Brand>(response.data) as unknown as PaginatedResponse<Brand>;
  }

  /**
   * Get a single brand by ID
   * @param id - Brand ID
   * @returns Brand data
   */
  async getById(id: string): Promise<Brand> {
    const response = await apiClient.get<ApiResponse<Brand>>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch brand');
  }

  /**
   * Create a new brand
   * @param data - Brand creation data
   * @returns Created brand
   */
  async create(data: CreateBrandRequest): Promise<Brand> {
    const response = await apiClient.post<ApiResponse<Brand>>(this.basePath, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to create brand');
  }

  /**
   * Update an existing brand
   * @param id - Brand ID
   * @param data - Brand update data
   * @returns Updated brand
   */
  async update(id: string, data: UpdateBrandRequest): Promise<Brand> {
    const response = await apiClient.patch<ApiResponse<Brand>>(`${this.basePath}/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update brand');
  }

  /**
   * Delete a brand
   * @param id - Brand ID
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);
  }

  /**
   * Search brands by name or contact person
   * @param query - Search query string
   * @param params - Additional list parameters
   * @returns Paginated list of matching brands
   */
  async search(
    query: string,
    params?: Omit<ListBrandsRequest, 'search'>
  ): Promise<PaginatedResponse<Brand>> {
    return this.list({
      ...params,
      search: query,
    });
  }

  /**
   * Get brands by relationship status
   * @param status - Relationship status to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of brands with specified status
   */
  async getByRelationshipStatus(
    status: string,
    params?: Omit<ListBrandsRequest, 'relationshipStatus'>
  ): Promise<PaginatedResponse<Brand>> {
    return this.list({
      ...params,
      relationshipStatus: status,
    });
  }

  /**
   * Get brand statistics
   * @param id - Brand ID
   * @returns Brand statistics including total deals and value
   */
  async getStats(id: string): Promise<{
    totalDeals: number;
    totalValue: number;
    activeDeals: number;
    completedDeals: number;
  }> {
    const response = await apiClient.get<
      ApiResponse<{
        totalDeals: number;
        totalValue: number;
        activeDeals: number;
        completedDeals: number;
      }>
    >(`${this.basePath}/${id}/stats`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch brand statistics');
  }

  /**
   * Get all deals for a specific brand
   * @param id - Brand ID
   * @param params - Pagination parameters
   * @returns List of deals for the brand
   */
  async getDeals(
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<unknown>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get<PaginatedResponse<unknown>>(
      `${this.basePath}/${id}/deals`,
      { params: queryParams }
    );

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to fetch brand deals');
  }

  /**
   * Archive a brand (soft delete)
   * @param id - Brand ID
   * @returns Updated brand with archived status
   */
  async archive(id: string): Promise<Brand> {
    const response = await apiClient.post<ApiResponse<Brand>>(`${this.basePath}/${id}/archive`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to archive brand');
  }

  /**
   * Restore an archived brand
   * @param id - Brand ID
   * @returns Restored brand
   */
  async restore(id: string): Promise<Brand> {
    const response = await apiClient.post<ApiResponse<Brand>>(`${this.basePath}/${id}/restore`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to restore brand');
  }
}

/**
 * Export singleton instance
 */
export const brandService = new BrandService();

/**
 * Export default
 */
export default brandService;
